/**
 * InventoryPage.tsx
 *
 * Full inventory management page for the staff-facing EHR/practice management system.
 * Supports product listing, filtering, pagination, CRUD operations, stock history,
 * supplier info display, barcode/SKU display, and bulk stock adjustment.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useAuth } from '@/hooks/useAuth';
import { usePageContext } from '@/hooks/usePageContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import type { FilterOption } from '@/types/filters.types';
import {
  INVENTORY_DASHBOARD_FILTER_CONFIGS,
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';
import { buildApiParams, extractSearch } from '@/lib/filterRegistry';
import { Download, Package } from 'lucide-react';
import {
  listProducts,
  listCategories,
  listProductTypes,
  listLowStockProducts,
  getAdjustmentHistory,
  adjustStock,
  type Product,
  type ProductListParams,
  type AdjustStockPayload,
  type AdjustmentType,
  type ManualAdjustmentType,
} from '@/services/inventory.service';
import { listFacilities } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import AdjustStockModal from './AdjustStockModal';
import StockStatusBadge from './StockStatusBadge';
import { WHOLE_UNITS_MESSAGE, checkStockQuantity } from './stockQuantity';

// ─── Types ────────────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'adjust'; product: Product }
  | { type: 'history'; product: Product }
  | { type: 'bulkAdjust' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${value.toFixed(2)}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAdjType(type: AdjustmentType): string {
  const map: Record<AdjustmentType, string> = {
    add: 'Add',
    remove: 'Remove',
    sale: 'Sale',
    return: 'Return',
    shrinkage: 'Shrinkage',
    adjustment: 'Adjustment',
    purchase_receive: 'Purchase Receive',
  };
  return map[type] ?? type;
}

const BULK_ADJUSTMENT_TYPE_OPTIONS: { value: ManualAdjustmentType; label: string }[] = [
  { value: 'add',        label: 'Add' },
  { value: 'remove',     label: 'Remove' },
  { value: 'sale',       label: 'Sale' },
  { value: 'return',     label: 'Return' },
  { value: 'shrinkage',  label: 'Shrinkage / Loss' },
  { value: 'adjustment', label: 'Set To' },
];

// ─── Stock History Modal ──────────────────────────────────────────────────────

function StockHistoryModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['inventory-history', product.productId],
    queryFn: () => getAdjustmentHistory(product.productId, 50),
    staleTime: 30_000,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Stock History</h2>
            <p className="text-sm text-slate-500">{product.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1">
          {/* Barcode / SKU display */}
          {product.sku && (
            <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Product SKU / Barcode</p>
              <p className="font-mono text-lg tracking-widest text-slate-800 select-all">{product.sku}</p>
            </div>
          )}

          {/* Current stock */}
          <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Current Stock</p>
              <p className="mt-0.5 text-2xl font-bold text-slate-900">{product.stockQuantity.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Low Stock Alert</p>
              <p className="mt-0.5 text-lg font-semibold text-slate-600">{product.lowStockAlert}</p>
            </div>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-400 py-4 text-center">Loading history...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No adjustment history recorded.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 font-medium text-slate-600">Date</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Type</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">Qty Change</th>
                    <th className="px-3 py-2 font-medium text-slate-600 text-right">New Qty</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Patient</th>
                    <th className="px-3 py-2 font-medium text-slate-600">Notes</th>
                    <th className="px-3 py-2 font-medium text-slate-600">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map((adj) => {
                    const qtyChange = adj.newQty - adj.previousQty;
                    const isPositive = qtyChange >= 0;
                    return (
                      <tr key={adj.adjustmentId} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-xs">{formatDate(adj.createdAt)}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              adj.adjustmentType === 'add' || adj.adjustmentType === 'return'
                                ? 'bg-green-100 text-green-800'
                                : adj.adjustmentType === 'adjustment'
                                ? 'bg-slate-100 text-slate-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {formatAdjType(adj.adjustmentType)}
                          </span>
                        </td>
                        <td className={`px-3 py-2 text-right font-medium ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                          {isPositive ? '+' : ''}{qtyChange}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{adj.newQty}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{adj.patientName ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 max-w-32 truncate">{adj.notes ?? '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{adj.userId ? `User #${adj.userId}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Stock Adjustment Modal ──────────────────────────────────────────────

function BulkAdjustModal({
  products,
  onClose,
  onSaved,
}: {
  products: Product[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [adjustments, setAdjustments] = useState<
    Array<{ productId: number; name: string; enabled: boolean; type: ManualAdjustmentType; quantity: string; notes: string }>
  >(
    products.slice(0, 20).map((p) => ({
      productId: p.productId,
      name: p.name,
      enabled: false,
      type: 'add' as ManualAdjustmentType,
      quantity: '',
      notes: '',
    })),
  );
  const [serverError, setServerError] = useState<string | null>(null);

  const bulkMutation = useMutation({
    mutationFn: async () => {
      const checked = adjustments
        .filter((a) => a.enabled)
        .map((a) => ({ ...a, check: checkStockQuantity(a.quantity) }));
      // Refuse the whole run before any request, so a fraction on one row
      // cannot leave the rows above it applied and the rest not.
      const fractional = checked.find((a) => !a.check.ok && a.check.reason === 'fraction');
      if (fractional) throw new Error(`${fractional.name}: ${WHOLE_UNITS_MESSAGE}`);

      const enabled = checked.flatMap((a) => (a.check.ok ? [{ ...a, quantity: a.check.quantity }] : []));
      if (enabled.length === 0) throw new Error('No adjustments selected.');
      for (const adj of enabled) {
        const payload: AdjustStockPayload = {
          adjustmentType: adj.type,
          quantity: adj.quantity,
          notes: adj.notes || undefined,
        };
        await adjustStock(adj.productId, payload);
      }
    },
    onSuccess: () => {
      onSaved();
    },
    onError: (err) => {
      setServerError(getErrorMessage(err, 'The bulk adjustment could not be applied. Please try again.'));
    },
  });

  const enabledCount = adjustments.filter((a) => a.enabled).length;

  const updateAdj = (idx: number, updates: Partial<(typeof adjustments)[0]>) => {
    setAdjustments((prev) => prev.map((a, i) => (i === idx ? { ...a, ...updates } : a)));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Bulk Stock Adjustment</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1">
          <p className="text-sm text-slate-600 mb-4">
            Select products and enter adjustment quantities. Only checked products will be adjusted.
          </p>

          {serverError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">
              {serverError}
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 w-8" />
                  <th className="px-3 py-2 font-medium text-slate-600 text-left">Product</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-left">Type</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-left">Quantity</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adjustments.map((adj, idx) => (
                  <tr key={adj.productId} className={adj.enabled ? 'bg-indigo-50' : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={adj.enabled}
                        onChange={(e) => updateAdj(idx, { enabled: e.target.checked })}
                        className="accent-indigo-600 h-4 w-4"
                      />
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{adj.name}</td>
                    <td className="px-3 py-2">
                      <select
                        value={adj.type}
                        onChange={(e) => updateAdj(idx, { type: e.target.value as ManualAdjustmentType })}
                        disabled={!adj.enabled}
                        className="border border-slate-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                      >
                        {BULK_ADJUSTMENT_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        step="1"
                        aria-label={`Quantity for ${adj.name}`}
                        value={adj.quantity}
                        onChange={(e) => updateAdj(idx, { quantity: e.target.value })}
                        disabled={!adj.enabled}
                        placeholder="0"
                        className="border border-slate-300 rounded px-2 py-1 text-xs w-20 focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={adj.notes}
                        onChange={(e) => updateAdj(idx, { notes: e.target.value })}
                        disabled={!adj.enabled}
                        placeholder="Optional..."
                        className="border border-slate-300 rounded px-2 py-1 text-xs w-full focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <span className="text-sm text-slate-500">{enabledCount} product{enabledCount !== 1 ? 's' : ''} selected</span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={bulkMutation.isPending}
              className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => bulkMutation.mutate()}
              disabled={bulkMutation.isPending || enabledCount === 0}
              className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {bulkMutation.isPending ? 'Applying...' : 'Apply Adjustments'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Inventory Stock');
  // userLevel >= 3. Reads stay open to everyone; only stock-lowering is gated.
  const { isAdmin } = useAuth();

  const filterState = useLocalDashboardFilters(INVENTORY_DASHBOARD_FILTER_CONFIGS, 'inventory-stock');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const filterParams = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  // Sorting is server-side: the list is paginated, and DataTable's client-side
  // sort is a no-op for columns that use `render` (all of these do).
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'inventory', entityLabel: 'inventory' });

  useEffect(() => { setPage(1); }, [filterParams]);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // ── Query params ──────────────────────────────────────────────────────────
  const params: ProductListParams = useMemo(
    () => ({ ...filterParams, page, perPage, sortBy, sortDir }),
    [filterParams, page, perPage, sortBy, sortDir],
  );

  // ── Data queries ──────────────────────────────────────────────────────────
  const {
    data: productsData,
    isLoading,
  } = useQuery({
    queryKey: ['inventory', params],
    queryFn: () => listProducts(params),
    placeholderData: (prev) => prev,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: listCategories,
    staleTime: 5 * 60 * 1000,
  });

  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: listFacilities,
    staleTime: 5 * 60 * 1000,
  });

  const { data: productTypes = [] } = useQuery({
    queryKey: ['product-types-lookup'],
    queryFn: listProductTypes,
    staleTime: 5 * 60 * 1000,
  });

  // Account-wide, filter-aware attention list. The tiles used to count only the
  // rows on the current page, so they disagreed with the dashboard and changed
  // as you paginated. Same source the dashboard reads, so the two now agree.
  const { data: attentionItems = [] } = useQuery({
    queryKey: ['inventory-low-stock', filterParams],
    queryFn: () => listLowStockProducts(filterParams),
    staleTime: 60 * 1000,
  });

  // ── Derived stats ─────────────────────────────────────────────────────────
  const products = productsData?.data ?? [];
  const meta = productsData?.meta;

  const totalProducts = meta?.total ?? 0;
  const lowStockCount = attentionItems.filter((p) => p.stockStatus === 'low').length;
  const outOfStockCount = attentionItems.filter((p) => p.stockStatus === 'out').length;
  // Page-scoped by nature — the API paginates rows rather than returning a
  // running total, so the label says so rather than implying an account total.
  const pageValue = products.reduce(
    (sum, p) => sum + p.unitPrice * p.stockQuantity,
    0,
  );

  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  // ── AI page context ────────────────────────────────────────────────────────
  useEffect(() => {
    const search = extractSearch(effectiveValues);
    const filterParts: string[] = [];
    if (search) filterParts.push(`Search: ${search}`);
    if (activeFilters) filterParts.push(activeFilters);
    updateAIContext({
      filters: filterParts.length > 0 ? filterParts.join(', ') : 'none',
      dataSummary: `${totalProducts} products, ${lowStockCount} low stock`,
    });
  }, [effectiveValues, activeFilters, totalProducts, lowStockCount, updateAIContext]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSaved = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
    setModal({ type: 'none' });
  }, [queryClient]);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  function handleExport() {
    startExport({ ...filterParams });
  }

  // ── Sidebar filter logic ──────────────────────────────────────────────────

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      category: categories.map((cat) => ({ value: cat, label: cat })),
      productType: productTypes.map((pt) => ({
        value: String(pt.productTypeID),
        label:
          pt.productTypePublicName ||
          pt.productTypeName ||
          `Product Type #${pt.productTypeID}`,
      })),
      facility: facilities.filter((f) => f.active).map((f) => ({
        value: String(f.facilityId),
        label: f.facilityName || `Facility #${f.facilityId}`,
      })),
      status: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
      stockLevel: [
        { value: 'out', label: 'Out of Stock' },
        { value: 'low', label: 'Low Stock' },
        { value: 'ok', label: 'In Stock' },
      ],
    }),
    [categories, productTypes, facilities],
  );

  // ── DataTable columns ─────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<Product>[]>(() => [
    {
      key: 'imageUrl',
      header: '',
      render: (row) =>
        row.imageUrl ? (
          <img
            src={row.imageUrl}
            alt=""
            className="h-10 w-10 rounded-md object-cover border border-slate-200 bg-slate-50"
            loading="lazy"
          />
        ) : (
          <div className="h-10 w-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300">
            <Package className="h-5 w-5" />
          </div>
        ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      // No `truncate` here. It wraps the whole cell in overflow-hidden/nowrap,
      // which ellipsized the name AND clipped the status badge sitting after it —
      // so a long-named out-of-stock item showed no badge at all.
      width: 'w-[22rem] max-w-[22rem]',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-slate-900 whitespace-normal break-words">
            {row.name}
          </span>
          <StockStatusBadge status={row.stockStatus} />
        </div>
      ),
    },
    {
      key: 'productTypeName',
      header: 'Product Type',
      sortable: true,
      render: (row) => (
        <span className="text-slate-600">{row.productTypeName ?? '—'}</span>
      ),
    },
    {
      key: 'sku',
      header: 'SKU / Barcode',
      render: (row) =>
        row.sku ? (
          <span className="font-mono text-xs tracking-wider text-slate-700 bg-slate-100 px-2 py-1 rounded select-all">
            {row.sku}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      width: 'w-56 max-w-[14rem]',
      render: (row) => (
        <span className="text-slate-600 whitespace-normal break-words">{row.category ?? '—'}</span>
      ),
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      sortable: true,
      align: 'right',
      render: (row) => <span className="text-slate-700">{formatCurrency(row.unitPrice)}</span>,
    },
    // No Cost column: `quote_item_costs` carries no cost-basis column, so the API
    // returned null for every row and the cell always read "—".
    {
      // A product has one stock count; the facility filter decides which
      // products are listed, not what the count reads. This column used to
      // switch to "Stock @ <facility>" and show a per-facility number the API
      // never sent, so every row read 0 once a facility was picked.
      key: 'stockQuantity',
      header: 'Stock Qty',
      sortable: true,
      align: 'right',
      render: (row) => {
        const qty = row.stockQuantity ?? 0;
        // Negatives are shown as-is: the shortfall is the size of the
        // reconciliation gap, and clamping it to 0 would hide that.
        return (
          <span className={qty <= 0 ? 'font-medium text-red-700' : 'font-medium text-slate-800'}>
            {qty.toLocaleString()}
          </span>
        );
      },
    },
    {
      key: 'lowStockAlert',
      header: 'Reorder Point',
      sortable: true,
      align: 'right',
      render: (row) => <span className="text-slate-600">{(row.lowStockAlert ?? 0).toLocaleString()}</span>,
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) =>
        row.active ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
            Inactive
          </span>
        ),
    },
  ], []);

  // ── Row actions ───────────────────────────────────────────────────────────

  const rowActions = useMemo<DataTableAction<Product>[]>(() => [
    {
      label: 'Adjust Stock',
      onClick: (row) => setModal({ type: 'adjust', product: row }),
    },
    {
      label: 'View History',
      onClick: (row) => setModal({ type: 'history', product: row }),
    },
  ], []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={INVENTORY_DASHBOARD_FILTER_CONFIGS}
            values={effectiveValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={filterState.setFilter}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            storageKey="inventory-stock"
            showAIAssistant={false}
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              title="Stock"
              actions={(
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleExport}
                    disabled={totalProducts === 0 || exporting}
                    loading={exporting}
                  >
                    Export
                  </Button>
                  {exportModal}
                  {/* Bulk adjust exists to write stock down across many rows at
                      once, so it follows the same level rule the API enforces. */}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      onClick={() => setModal({ type: 'bulkAdjust' })}
                    >
                      Bulk Adjust Stock
                    </Button>
                  )}
                </div>
              )}
            />

            <MetricCardGrid columns={4}>
              <StatCard label="Total Products" value={totalProducts} />
              <StatCard label="Out of Stock" value={outOfStockCount} />
              <StatCard label="Low Stock" value={lowStockCount} />
              <StatCard label="Value (this page)" value={formatCurrency(pageValue)} />
            </MetricCardGrid>

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<Product>
              data={products}
              columns={columns}
              keyField="productId"
              loading={isLoading}
              rowActions={rowActions}
              manualSort
              sortKey={sortBy}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              page={page}
              pageSize={perPage}
              total={totalProducts}
              onPageChange={(p) => setPage(p)}
              pageSizeOptions={[25, 50, 100, 200]}
              onPageSizeChange={(size) => {
                setPerPage(size);
                setPage(1);
              }}
              emptyTitle="No products found"
              emptyDescription="Try adjusting the filters or search term."
            />
          </div>
        </div>
      </ListPageLayout>

      {/* ── Modals ── */}

      {/* Adjust stock modal */}
      {modal.type === 'adjust' && (
        <AdjustStockModal
          open
          onClose={() => setModal({ type: 'none' })}
          onSaved={handleSaved}
          product={modal.product}
        />
      )}

      {/* Stock history modal */}
      {modal.type === 'history' && (
        <StockHistoryModal
          product={modal.product}
          onClose={() => setModal({ type: 'none' })}
        />
      )}

      {/* Bulk adjust modal */}
      {modal.type === 'bulkAdjust' && (
        <BulkAdjustModal
          products={products}
          onClose={() => setModal({ type: 'none' })}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
